// src/components/admin/branding/UnauthenticatedContent.tsx
import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Collapse,
  Alert,
  Divider,
  Paper,
} from '@mui/material';

interface UnauthenticatedContentProps {
  useCustom: boolean;
  header: string;
  footer: string;
  onToggleUseCustom: (checked: boolean) => void;
  onHeaderChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFooterChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const UnauthenticatedContent: React.FC<UnauthenticatedContentProps> = ({
  useCustom,
  header,
  footer,
  onToggleUseCustom,
  onHeaderChange,
  onFooterChange,
}) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Unauthenticated Pages Content
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Configure how header and footer appear for users who are not logged in.
      </Typography>

      <Paper sx={{ p: 3, mb: 3, bgcolor: 'background.paper' }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2">
            How unauthenticated content works:
          </Typography>
          <ul>
            <li>
              When a user is <strong>logged in</strong>, they always see the
              regular custom header and footer (if provided).
            </li>
            <li>
              When a user is <strong>not logged in</strong>, you have two
              options:
            </li>
          </ul>
          <Box sx={{ pl: 4 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Option 1:</strong> Toggle the switch below to ON to use
              the same header and footer for both authenticated and
              unauthenticated users. This saves you from maintaining duplicate
              content.
            </Typography>
            <Typography variant="body2">
              <strong>Option 2:</strong> Toggle the switch below to OFF and
              provide specific header and footer content that only appears for
              users who aren't logged in.
            </Typography>
          </Box>
        </Alert>

        <FormControlLabel
          control={
            <Switch
              checked={useCustom}
              onChange={(e) => onToggleUseCustom(e.target.checked)}
              name="usecustomheaderandfooterunauthenticated"
            />
          }
          label="Use the same header and footer for both authenticated and unauthenticated users"
        />

        <Collapse in={!useCustom} sx={{ mt: 3 }}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Custom content specifically for unauthenticated users
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These elements will only appear when users are not logged in. If
            left empty, no header/footer will be shown to unauthenticated users.
          </Typography>

          <TextField
            fullWidth
            name="customunauthenticatedheader"
            label="Unauthenticated Header"
            multiline
            rows={4}
            value={header || ''}
            onChange={onHeaderChange}
            helperText="Custom HTML for the header on unauthenticated pages"
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            name="customunauthenticatedfooter"
            label="Unauthenticated Footer"
            multiline
            rows={4}
            value={footer || ''}
            onChange={onFooterChange}
            helperText="Custom HTML for the footer on unauthenticated pages"
          />
        </Collapse>
      </Paper>
    </Box>
  );
};

export default UnauthenticatedContent;
